import autoSelectSeats from './autoSelectSeats';
import { PASSENGER_COUNTS, POSITION_ORDER } from './seatPreferenceConstants';
import { describeSeatLayout, normalizeSeatNumber } from './seatPreferenceMetadata';
import { isSeatOccupied } from './seats';

export const INDIVIDUAL_MATCH = Object.freeze({
  EXACT: 'exact same selection',
  FLOOR_AND_POSITION: 'floor and position',
  FLOOR: 'floor',
  POSITION: 'position',
  ADJACENCY: 'adjacency',
  FALLBACK: 'fallback',
});

const normalizeFloor = (value) => {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  }
  if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) return null;

  const floor = Number(value.trim());
  return Number.isSafeInteger(floor) && floor > 0 ? String(floor) : null;
};

const normalizePositions = (value) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  const positions = new Set(value.filter((position) => POSITION_ORDER.includes(position)));
  return positions.size ? POSITION_ORDER.filter((position) => positions.has(position)) : null;
};

const positionsIntersect = (left, right) =>
  left !== null && right !== null && left.some((position) => right.includes(position));

const positionsEqual = (left, right) =>
  left !== null &&
  right !== null &&
  left.length === right.length &&
  left.every((position) => right.includes(position));

const normalizePreference = (preference = {}) => ({
  number: normalizeSeatNumber(preference.number),
  floor: normalizeFloor(preference.floor),
  position: normalizePositions(preference.position),
});

const scoreNormalizedPreference = (candidatePreference, savedPreference) => {
  const sameFloor =
    candidatePreference.floor !== null &&
    savedPreference.floor !== null &&
    candidatePreference.floor === savedPreference.floor;
  const intersectingPositions = positionsIntersect(
    candidatePreference.position,
    savedPreference.position,
  );

  if (
    candidatePreference.number !== null &&
    savedPreference.number !== null &&
    candidatePreference.number === savedPreference.number &&
    sameFloor &&
    positionsEqual(candidatePreference.position, savedPreference.position)
  ) {
    return { tier: 0, match: INDIVIDUAL_MATCH.EXACT };
  }
  if (sameFloor && intersectingPositions) {
    return { tier: 1, match: INDIVIDUAL_MATCH.FLOOR_AND_POSITION };
  }
  if (sameFloor) return { tier: 2, match: INDIVIDUAL_MATCH.FLOOR };
  if (intersectingPositions) return { tier: 3, match: INDIVIDUAL_MATCH.POSITION };
  return { tier: 4, match: INDIVIDUAL_MATCH.FALLBACK };
};

/**
 * Scores one current physical seat against one stored preference.
 * Unknown dimensions never establish equality and number alone is not a match.
 * @param {Object} candidatePreference Current descriptor metadata.
 * @param {Object} savedPreference Validated stored metadata.
 * @returns {{tier: number, match: string}} Accepted tier and label.
 */
export const scoreIndividualPreference = (candidatePreference = {}, savedPreference = {}) => {
  const candidateNumber = normalizeSeatNumber(candidatePreference.number);
  const savedNumber = normalizeSeatNumber(savedPreference.number);
  const candidateFloor = normalizeFloor(candidatePreference.floor);
  const savedFloor = normalizeFloor(savedPreference.floor);
  const candidatePosition = normalizePositions(candidatePreference.position);
  const savedPosition = normalizePositions(savedPreference.position);

  return scoreNormalizedPreference(
    { number: candidateNumber, floor: candidateFloor, position: candidatePosition },
    { number: savedNumber, floor: savedFloor, position: savedPosition },
  );
};

const buildScoreCache = (candidates, saved) => {
  if (saved.length === 0) return new Map();

  const normalizedSaved = saved.map(normalizePreference);
  return new Map(
    candidates.map((descriptor) => {
      const normalizedCandidate = normalizePreference(descriptor.preference);
      return [
        descriptor,
        normalizedSaved.map((savedPreference) => {
          return scoreNormalizedPreference(normalizedCandidate, savedPreference);
        }),
      ];
    }),
  );
};

const coordinateKey = (id) => JSON.stringify(id);

const isLegacyEligible = ({ seat }) =>
  seat.category !== 'hallway' && !seat.petFriendly && !isSeatOccupied(seat);

const validateDescriptors = (descriptors) => {
  if (!Array.isArray(descriptors)) throw new TypeError('Seat descriptors must be an array');

  const coordinates = new Set();
  descriptors.forEach((descriptor) => {
    if (
      !descriptor ||
      !descriptor.seat ||
      !Array.isArray(descriptor.id) ||
      !Array.isArray(descriptor.neighbors) ||
      !descriptor.preference ||
      !Number.isFinite(descriptor.order)
    ) {
      throw new Error('Invalid seat descriptor');
    }

    const key = coordinateKey(descriptor.id);
    if (coordinates.has(key)) throw new Error('Duplicate seat descriptor coordinate');
    coordinates.add(key);
  });
};

const clonePreference = ({ number, floor, position }) => ({
  number,
  floor,
  position: position ? [...position] : null,
});

const buildMatch = (descriptor, match, passengerIndex) => ({
  passengerIndex,
  seatId: [...descriptor.id],
  preference: clonePreference(descriptor.preference),
  match,
});

const getAmbiguousReservationKeys = (descriptors) => {
  const counts = new Map();
  descriptors.forEach(({ reservationKey }) => {
    if (reservationKey) counts.set(reservationKey, (counts.get(reservationKey) || 0) + 1);
  });
  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([reservationKey]) => reservationKey),
  );
};

const unsupportedIdentity = (message) => {
  const error = new Error(message);
  error.code = 'unsupported-smart-seat-identity';
  return error;
};

const findLegacyDescriptor = (seat, availableDescriptors) => {
  const sameReference = availableDescriptors.filter((descriptor) => descriptor.seat === seat);
  if (sameReference.length === 1) return sameReference[0];

  const number = normalizeSeatNumber(seat && seat.number);
  const index = seat && seat.index;
  if (number === null || !Number.isFinite(index)) return null;

  const matches = availableDescriptors.filter(
    (descriptor) =>
      descriptor.seat.index === index &&
      normalizeSeatNumber(descriptor.seat.number) === number &&
      descriptor.seat.seatLevel === seat.seatLevel,
  );
  if (matches.length === 1) return matches[0];

  const canonicalMatches = availableDescriptors.filter(
    (descriptor) =>
      descriptor.reservationKey &&
      normalizeSeatNumber(descriptor.seat.number) === number &&
      descriptor.seat.seatLevel === seat.seatLevel,
  );
  return canonicalMatches.length === 1 ? canonicalMatches[0] : null;
};

const attachLegacyMatches = (seats, availableDescriptors, ambiguousReservationKeys) => {
  const usedCoordinates = new Set();
  const usedReservationKeys = new Set();
  const matches = seats.map((seat, passengerIndex) => {
    const descriptor = findLegacyDescriptor(seat, availableDescriptors);
    if (!descriptor) {
      throw unsupportedIdentity('Legacy fallback seat is not present in the raw layout');
    }
    if (!descriptor.reservationKey) {
      throw unsupportedIdentity('Legacy fallback seat has an unsupported reservation identity');
    }
    if (ambiguousReservationKeys.has(descriptor.reservationKey)) {
      throw unsupportedIdentity('Legacy fallback seat has an ambiguous reservation identity');
    }

    const coordinate = coordinateKey(descriptor.id);
    if (usedCoordinates.has(coordinate)) {
      throw unsupportedIdentity('Legacy fallback reused a physical seat');
    }
    if (usedReservationKeys.has(descriptor.reservationKey)) {
      throw unsupportedIdentity('Legacy fallback reused an ambiguous reservation identity');
    }

    usedCoordinates.add(coordinate);
    usedReservationKeys.add(descriptor.reservationKey);
    return buildMatch(descriptor, INDIVIDUAL_MATCH.FALLBACK, passengerIndex);
  });

  return { seats, matches };
};

const runLegacyFallback = (
  legacyFallback,
  passengerCount,
  availableDescriptors,
  ambiguousReservationKeys,
) => {
  if (typeof legacyFallback !== 'function') {
    throw new TypeError('A legacy fallback callback is required');
  }
  const seats = legacyFallback();
  if (seats === null) return null;
  if (!Array.isArray(seats) || seats.length !== passengerCount) {
    throw new Error('Legacy fallback returned an incomplete seat selection');
  }
  return attachLegacyMatches(seats, availableDescriptors, ambiguousReservationKeys);
};

const resolveSoloPreference = (preferences) => {
  if (Array.isArray(preferences?.['1']) && preferences['1'].length === 1) {
    return preferences['1'][0];
  }
  if (Array.isArray(preferences?.['2']) && preferences['2'].length === 2) {
    return preferences['2'][0];
  }
  return null;
};

const resolvePairMode = (preferences) => {
  if (Array.isArray(preferences?.['2']) && preferences['2'].length === 2) {
    return { mode: 'pair', saved: preferences['2'] };
  }
  if (Array.isArray(preferences?.['1']) && preferences['1'].length === 1) {
    return { mode: 'anchor', saved: [preferences['1'][0]] };
  }
  return { mode: 'none', saved: [] };
};

const compareAscending = (left, right) => {
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return left[index] - right[index];
  }
  return 0;
};

const compareHistogram = (left, right) => {
  for (let tier = 0; tier < left.length; tier += 1) {
    if (left[tier] !== right[tier]) return right[tier] - left[tier];
  }
  return 0;
};

const isBetterAssignment = (candidate, best, mode) => {
  if (!best) return true;
  if (mode === 'pair') {
    const histogramComparison = compareHistogram(candidate.histogram, best.histogram);
    if (histogramComparison !== 0) return histogramComparison < 0;
  }
  return compareAscending(candidate.tieBreak, best.tieBreak) < 0;
};

const scoreAssignment = (first, second, mode, scoreCache, adjacent) => {
  const candidates = [first, second];
  let scores = [];
  if (mode === 'pair') {
    scores = [scoreCache.get(first)[0], scoreCache.get(second)[1]];
  } else if (mode === 'anchor') {
    scores = [scoreCache.get(first)[0]];
  }
  const histogram = [0, 0, 0, 0];
  scores.forEach(({ tier }) => {
    if (tier < histogram.length) histogram[tier] += 1;
  });

  let tieBreak = [first.order, second.order];
  if (mode === 'pair') tieBreak = [...scores.map(({ tier }) => tier), ...tieBreak];
  if (mode === 'anchor') tieBreak = [scores[0].tier, ...tieBreak];

  return { adjacent, candidates, scores, histogram, tieBreak };
};

const updateBestAssignments = (best, assignment, mode) => {
  if (
    mode === 'pair' &&
    assignment.scores.every(({ tier }) => tier === 0) &&
    isBetterAssignment(assignment, best.exact, mode)
  ) {
    best.exact = assignment;
  }
  if (assignment.adjacent && isBetterAssignment(assignment, best.adjacent, mode)) {
    best.adjacent = assignment;
  }
  if (mode !== 'none' && isBetterAssignment(assignment, best.separate, mode)) {
    best.separate = assignment;
  }
};

const buildPairResult = (assignment, mode) => ({
  seats: assignment.candidates.map(({ seat }) => seat),
  matches: assignment.candidates.map((descriptor, passengerIndex) => {
    let match = INDIVIDUAL_MATCH.FALLBACK;
    if (mode === 'pair') {
      const score = assignment.scores[passengerIndex];
      match = score.tier === 4 && assignment.adjacent ? INDIVIDUAL_MATCH.ADJACENCY : score.match;
    } else if (mode === 'anchor') {
      match = passengerIndex === 0 ? assignment.scores[0].match : INDIVIDUAL_MATCH.FALLBACK;
      if (passengerIndex === 0 && assignment.adjacent && assignment.scores[0].tier === 4) {
        match = INDIVIDUAL_MATCH.ADJACENCY;
      }
      if (passengerIndex === 1 && assignment.adjacent) match = INDIVIDUAL_MATCH.ADJACENCY;
    } else if (assignment.adjacent) {
      match = INDIVIDUAL_MATCH.ADJACENCY;
    }
    return buildMatch(descriptor, match, passengerIndex);
  }),
});

const areAdjacent = (left, right) => {
  const leftKey = coordinateKey(left.id);
  const rightKey = coordinateKey(right.id);
  return (
    left.neighbors.some((neighbor) => coordinateKey(neighbor) === rightKey) ||
    right.neighbors.some((neighbor) => coordinateKey(neighbor) === leftKey)
  );
};

/**
 * Selects an ordered pair from saved preferences or physical adjacency, falling back only when
 * ranking cannot produce a safe pair. A physical edge can be selected without saved preferences.
 * @param {Object} options Selection inputs.
 * @param {Array<Object>} options.descriptors Seat descriptors validated before ranking.
 * @param {Object<string, Array<Object>>} [options.preferences={}] Validated count buckets.
 * @param {Function} [options.legacyFallback] Legacy selector invoked only on the fallback path.
 * @returns {{seats: Array<Object>, matches: Array<Object>}|null} Seats and matches in
 * passenger/saved-slot order, or null when the invoked fallback reports a shortage.
 * @throws {TypeError} If descriptors is not an array or the fallback path lacks a function.
 * @throws {Error} If descriptors are malformed or duplicated, or an invoked fallback result is
 * incomplete or has an unsupported identity. Errors thrown by the invoked callback also propagate.
 */
export const selectSeatPair = ({ descriptors, preferences = {}, legacyFallback }) => {
  validateDescriptors(descriptors);
  const ambiguousReservationKeys = getAmbiguousReservationKeys(descriptors);
  const availableDescriptors = descriptors.filter(isLegacyEligible);
  const candidates = availableDescriptors.filter(
    ({ reservationKey }) => reservationKey && !ambiguousReservationKeys.has(reservationKey),
  );
  const { mode, saved } = resolvePairMode(preferences);
  const scoreCache = candidates.length >= 2 ? buildScoreCache(candidates, saved) : new Map();
  const best = { exact: null, adjacent: null, separate: null };

  for (let leftIndex = 0; leftIndex < candidates.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < candidates.length; rightIndex += 1) {
      const left = candidates[leftIndex];
      const right = candidates[rightIndex];
      // Candidate filtering removes every key duplicated anywhere in the raw descriptor domain.
      const adjacent = areAdjacent(left, right);
      updateBestAssignments(best, scoreAssignment(left, right, mode, scoreCache, adjacent), mode);
      updateBestAssignments(best, scoreAssignment(right, left, mode, scoreCache, adjacent), mode);
    }
  }

  if (best.exact) return buildPairResult(best.exact, mode);
  if (best.adjacent) return buildPairResult(best.adjacent, mode);
  if (best.separate) return buildPairResult(best.separate, mode);
  return runLegacyFallback(legacyFallback, 2, availableDescriptors, ambiguousReservationKeys);
};

/**
 * Uses the solo bucket, or the first saved pair slot, to select one seat; invokes the legacy
 * selector when neither preference exists or no non-fallback match exists.
 * @param {Object} options Selection inputs.
 * @param {Array<Object>} options.descriptors Seat descriptors validated before ranking.
 * @param {Object<string, Array<Object>>} [options.preferences={}] Validated count buckets.
 * @param {Function} [options.legacyFallback] Legacy selector invoked only on the fallback path.
 * @returns {{seats: Array<Object>, matches: Array<Object>}|null} One ordered seat and match, or
 * null when the invoked fallback reports a shortage.
 * @throws {TypeError} If descriptors is not an array or the fallback path lacks a function.
 * @throws {Error} If descriptors are malformed or duplicated, or an invoked fallback result is
 * incomplete or has an unsupported identity. Errors thrown by the invoked callback also propagate.
 */
export const selectSoloSeat = ({ descriptors, preferences = {}, legacyFallback }) => {
  validateDescriptors(descriptors);
  const ambiguousReservationKeys = getAmbiguousReservationKeys(descriptors);
  const availableDescriptors = descriptors.filter(isLegacyEligible);
  const candidates = availableDescriptors.filter(
    ({ reservationKey }) => reservationKey && !ambiguousReservationKeys.has(reservationKey),
  );
  const saved = resolveSoloPreference(preferences);
  if (!saved) {
    return runLegacyFallback(legacyFallback, 1, availableDescriptors, ambiguousReservationKeys);
  }

  const scoreCache = candidates.length ? buildScoreCache(candidates, [saved]) : new Map();
  let best = null;
  candidates.forEach((descriptor) => {
    const score = scoreCache.get(descriptor)[0];
    if (
      !best ||
      score.tier < best.score.tier ||
      (score.tier === best.score.tier && descriptor.order < best.descriptor.order)
    ) {
      best = { descriptor, score };
    }
  });

  if (!best || best.score.tier === 4) {
    return runLegacyFallback(legacyFallback, 1, availableDescriptors, ambiguousReservationKeys);
  }
  return {
    seats: [best.descriptor.seat],
    matches: [buildMatch(best.descriptor, best.score.match, 0)],
  };
};

/**
 * Selects one or two preferred seats without reading storage or changing application state.
 * @param {Array} layout Raw seat layout.
 * @param {string} tripSlug Current trip identity.
 * @param {number} passengerCount Supported booking count.
 * @param {Object<string, Array<Object>>} preferences Validated one/two count buckets.
 * @param {Object} [geometryOptions] Layout-family options.
 * @returns {{seats: Array<Object>, matches: Array<Object>}|null} Complete ordered result.
 */
const selectPreferredSeats = (
  layout,
  tripSlug,
  passengerCount,
  preferences = {},
  geometryOptions = {},
) => {
  if (!PASSENGER_COUNTS.includes(passengerCount)) return null;

  const descriptors = describeSeatLayout(layout, { ...geometryOptions, tripSlug });
  const input = {
    descriptors,
    preferences,
    legacyFallback: () => autoSelectSeats(layout, tripSlug, passengerCount),
  };
  return passengerCount === 1 ? selectSoloSeat(input) : selectSeatPair(input);
};

export default selectPreferredSeats;
